import { useCallback, useEffect, useRef, useState } from 'react';
import { Box, Button } from '@mui/material';
import { useTranslation } from 'react-i18next';
import { ConfirmationDialog, NewPageTitleBar } from '@/components';
import { getMenuData } from '@core/data/menuData.ts';
import { FCSecurePage, useRegisterRefresh } from '@/components/shared/navigation/securePage.ts';
import { DateTime } from 'luxon';
import { DeleteConfirmationDialog } from '@/components/dialog/DeleteConfirmationDialog.tsx';
import LogicServiceEditor from './components/logicservice/LogicServiceEditor';
import LogicServiceMainTable from './components/logicservice/LogicServiceMainTable';
import LogicServiceUnassignedShopsTable from './components/logicservice/LogicServiceUnassignedShopsTable';
import {
    logicServiceManagementService,
    LogicServiceLayout,
    LogicServiceVersion,
    UnassignedShop
} from '@/services/logicServiceManagementService';
import { useSettingsStore } from '@/core/store';
import { useNotifications } from '@core/hooks/useNotifications.ts';

type PanelMode = 'add' | 'edit' | null;

const LogicServiceManagement: FCSecurePage = () => {
    const { t } = useTranslation();

    const [layouts, setLayouts] = useState<LogicServiceLayout[]>([]);
    const [unassignedShops, setUnassignedShops] = useState<UnassignedShop[]>([]);
    const [loading, setLoading] = useState(false);
    const [lastUpdate, setLastUpdate] = useState<DateTime | undefined>(undefined);
    const [panelMode, setPanelMode] = useState<PanelMode>(null);
    const [editTarget, setEditTarget] = useState<LogicServiceLayout | null>(null);
    const [activeHistoryIds, setActiveHistoryIds] = useState<Set<string>>(new Set());
    const [isSaving, setIsSaving] = useState(false);
    const { notifySuccess, notifyError } = useNotifications();

    const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
    const [deleteTarget, setDeleteTarget] = useState<LogicServiceLayout | null>(null);

    const confirmationDialogRef = useRef<any>(null);
    const [downloadTarget, setDownloadTarget] = useState<LogicServiceLayout | null>(null);

    const editorRef = useRef<HTMLDivElement>(null);

    const { isMobile } = useSettingsStore();

    useEffect(() => {
        setActiveHistoryIds(new Set());
    }, [isMobile]);

    const load = useCallback(async () => {
        setLoading(true);
        try {
            const { layouts: data, unassignedShops: unassigned } = await logicServiceManagementService.loadAll();
            setLayouts(data);
            setUnassignedShops(unassigned);
            setLastUpdate(DateTime.now());
        } catch (error) {
            console.error('[LogicServiceManagement] Failed to load layouts:', error);
        } finally {
            setLoading(false);
        }
    }, []);

    useRegisterRefresh(load, loading);

    useEffect(() => {
        load();
    }, [load]);

    useEffect(() => {
        if (panelMode) {
            setTimeout(() => {
                if (!editorRef.current) return;

                const scrollContainer = document.querySelector('main') as HTMLElement;

                if (!scrollContainer) return;

                const top =
                    editorRef.current.getBoundingClientRect().top -
                    scrollContainer.getBoundingClientRect().top +
                    scrollContainer.scrollTop -
                    24;

                scrollContainer.scrollTo({ top, behavior: 'smooth' });
            }, 50);
        }
    }, [panelMode]);

    const openAddPanel = useCallback(() => {
        setEditTarget(null);
        setPanelMode('add');
    }, []);

    const openEditPanel = useCallback((layout: LogicServiceLayout) => {
        setEditTarget(layout);
        setPanelMode('edit');
    }, []);

    const closePanel = useCallback(() => {
        setPanelMode(null);
        setEditTarget(null);
    }, []);

    const handlePanelSave = useCallback(
        async (data: { name: string; configJson: string; description: string }) => {
            setIsSaving(true);
            try {
                if (panelMode === 'add') {
                    const newLayout = await logicServiceManagementService.createLayout(data);
                    setLayouts((prev) => [...prev, newLayout]);
                } else if (panelMode === 'edit' && editTarget) {
                    const updated = await logicServiceManagementService.updateLayout(editTarget, data);
                    setLayouts((prev) => prev.map((l) => (l.id === editTarget.id ? updated : l)));
                }
                closePanel();
                notifySuccess(t('layoutSavedSuccessfully'));
            } catch (error) {
                console.error('[LogicServiceManagement] Failed to save layout:', error);
                notifyError(t('pleaseTryAgain'));
            } finally {
                setIsSaving(false);
            }
        },
        [panelMode, editTarget, closePanel, t]
    );

    const handleDeleteClick = useCallback((layout: LogicServiceLayout) => {
        setDeleteTarget(layout);
        setDeleteDialogOpen(true);
    }, []);

    const handleDeleteConfirm = useCallback(async () => {
        if (!deleteTarget) return;
        try {
            await logicServiceManagementService.deleteLayout(deleteTarget.id);
            setLayouts((prev) => prev.filter((l) => l.id !== deleteTarget.id));
            notifySuccess(t('layoutDeletedSuccessfully'));
        } catch (error) {
            console.error('[LogicServiceManagement] Failed to delete layout:', error);
            notifyError(t('deleteFailed'));
        } finally {
            setDeleteTarget(null);
            setDeleteDialogOpen(false);
        }
    }, [deleteTarget, t]);

    const handleDeleteCancel = useCallback(() => {
        setDeleteTarget(null);
        setDeleteDialogOpen(false);
    }, []);

    const handleDownload = useCallback((layout: LogicServiceLayout) => {
        setDownloadTarget(layout);
        confirmationDialogRef.current?.openDialog();
    }, []);

    const handleDownloadConfirm = useCallback(() => {
        if (downloadTarget) {
            logicServiceManagementService.downloadLayout(downloadTarget);
        }
        setDownloadTarget(null);
    }, [downloadTarget]);

    const handleDownloadCancel = useCallback(() => {
        setDownloadTarget(null);
    }, []);

    const handleHistoryDownload = useCallback((layoutName: string, version: LogicServiceVersion) => {
        logicServiceManagementService.downloadHistoryVersion(layoutName, version);
    }, []);

    const handleHistoryToggle = useCallback((layoutId: string) => {
        setActiveHistoryIds((prev) => {
            const next = new Set(prev);
            if (next.has(layoutId)) {
                next.delete(layoutId);
            } else {
                next.add(layoutId);
            }
            return next;
        });
    }, []);

    return (
        <Box>
            <NewPageTitleBar
                icon={<i className="bi bi-laptop" />}
                title={t('logicServiceManagement')}
                menus={getMenuData()}
                currentRoute={{ path: '/manage-organisation/logic-service-management' }}
                iconSize="20px"
                t={t}
                lastUpdate={lastUpdate}
            />

            <Box sx={{ width: '100%', px: 4, pb: 3 }}>
                <Box sx={{ pb: 1, '@media (max-width: 480px)': { pt: 3 } }}>
                    <Button
                        variant="text"
                        onClick={openAddPanel}
                        disabled={!!panelMode}
                        className="pagetitlebar-action-btn pagetitlebar-action-btn--default"
                    >
                        <Box className="pagetitlebar-action-btn-icon">
                            <i
                                className="bi bi-plus-lg"
                                style={{ fontSize: '16px', color: 'inherit', lineHeight: 1, paddingRight: 6 }}
                            />
                        </Box>
                        <span>{t('addLayout')}</span>
                    </Button>
                </Box>

                <LogicServiceMainTable
                    layouts={layouts}
                    loading={loading}
                    activeHistoryIds={activeHistoryIds}
                    onEdit={openEditPanel}
                    onDownload={handleDownload}
                    onHistoryToggle={handleHistoryToggle}
                    onHistoryDownload={handleHistoryDownload}
                    onDelete={handleDeleteClick}
                />

                {panelMode && (
                    <Box ref={editorRef}>
                        <LogicServiceEditor
                            mode={panelMode}
                            layout={editTarget}
                            onSave={handlePanelSave}
                            onCancel={closePanel}
                            isSaving={isSaving}
                        />
                    </Box>
                )}

                {unassignedShops.length > 0 && <LogicServiceUnassignedShopsTable unassignedShops={unassignedShops} />}
            </Box>

            <DeleteConfirmationDialog
                open={deleteDialogOpen}
                heading={t('deleteLayout')}
                message={t('deleteLayoutConfirm')}
                onConfirm={handleDeleteConfirm}
                onCancel={handleDeleteCancel}
            />

            <ConfirmationDialog
                dialogRef={confirmationDialogRef}
                heading={t('downloadLayout')}
                message={t('downloadLayoutConfirm')}
                onConfirm={handleDownloadConfirm}
                onClose={handleDownloadCancel}
                cancelText="cancel"
                confirmText="download"
            />
        </Box>
    );
};

LogicServiceManagement.displayName = 'Logic Service Management';
export default LogicServiceManagement;
